import { zodResolver } from "@hookform/resolvers/zod";
import { Link, Stack } from "expo-router";
import { Controller, useForm } from "react-hook-form";
import { useEffect } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Text,
  View,
  ActivityIndicator,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { Ionicons } from "@expo/vector-icons";
import { resolveStorageUrl } from "@lezz/supabase";
import { useCSSVariable } from "uniwind";

import { BackButton } from "@/shared/components/ui/back-button";
import { Heading } from "@/shared/components/ui/heading";
import { IconButton } from "@/shared/components/ui/icon-button";
import { useBack } from "@/shared/hooks/use-back";
import { useUpdateProfile, useProfile } from "@/features/profile/api/profile";
import { posthog } from "@/shared/lib/posthog";
import { isLocalFileUri } from "@/features/profile/api/storage";
import { Button } from "@/shared/components/ui/button";
import { FormInput } from "@/shared/components/ui/form-input";
import { FormSelect } from "@/shared/components/ui/form-select";
import { Toggle } from "@/shared/components/ui/toggle";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import {
  ProfileUpdateFormData as UpdateProfileFormData,
  profileUpdateSchema as updateProfileSchema,
} from "@/features/profile/validation/schema";
import { useStates } from "@/features/location/api/states";
import { useCitiesByState } from "@/features/location/api/cities";
import { DEFAULT_COUNTRY } from "@lezz/shared/constants";
import { AvatarPicker } from "@/features/profile/components/avatar-picker";

function getAvatarUri(url: string | null | undefined): string | null {
  if (!url) return null;
  if (isLocalFileUri(url)) return url;
  return resolveStorageUrl(url);
}

export default function ProfileEditScreen() {
  const { session } = useAuthContext();
  const goBack = useBack();
  const insets = useSafeAreaInsets();
  const foreground = useCSSVariable("--color-foreground") as string;
  const mutedForeground = useCSSVariable("--color-muted-foreground") as string;

  const updateProfile = useUpdateProfile();
  const { data: profile, isLoading: isLoadingProfile } = useProfile(
    session?.user.id
  );
  const { data: states = [], isLoading: isLoadingStates } = useStates();

  const {
    control,
    handleSubmit,
    setError,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<UpdateProfileFormData>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      username: "",
      displayName: "",
      city: "",
      state: "",
      country: DEFAULT_COUNTRY,
      instagramUsername: "",
      shouldShowInstagram: false,
      avatarUrl: "",
    },
  });

  useEffect(() => {
    if (profile) {
      reset({
        ...profile,
        country: profile.country ?? DEFAULT_COUNTRY,
      });
    }
  }, [profile, reset]);

  const selectedState = watch("state");
  const { data: cities = [], isLoading: isLoadingCities } =
    useCitiesByState(selectedState);

  useEffect(() => {
    if (selectedState && profile?.state !== selectedState) {
      setValue("city", "");
    }
  }, [selectedState, setValue, profile?.state]);

  const stateOptions = states.map((state) => ({
    label: state,
    value: state,
  }));

  const cityOptions = cities.map((city) => ({
    label: city.cityName,
    value: city.cityName,
  }));

  const onSubmit = async (data: UpdateProfileFormData) => {
    if (!session?.user.id) {
      setError("root", {
        message: "Você precisa estar logada para atualizar o perfil.",
      });
      return;
    }

    updateProfile.mutate(
      { userId: session?.user.id, data },
      {
        onSuccess: () => {
          posthog?.capture("profile_updated");
          goBack();
        },
        onError: (error) => {
          if (error.message === "username_taken") {
            setError("username", {
              type: "manual",
              message: "Esse nome de usuário já está em uso. Escolha outro.",
            });
          } else {
            setError("root", {
              message: "Não foi possível salvar o perfil. Tente novamente.",
            });
          }
        },
      }
    );
  };

  if (isLoadingProfile) {
    return (
      <View className="bg-background flex-1 items-center justify-center">
        <ActivityIndicator size="large" className="text-brand" />
      </View>
    );
  }

  if (!profile) {
    return (
      <View className="bg-background flex-1 items-center justify-center">
        <Text className="text-brand text-center text-base">
          Failed to load profile
        </Text>
      </View>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <ScreenContainer style={{ paddingTop: 0 }}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          className="flex-1"
        >
          <ScrollView
            contentContainerClassName="grow pb-8"
            contentInsetAdjustmentBehavior="never"
            keyboardShouldPersistTaps="handled"
          >
            {/* Header */}
            <View
              className="flex-row items-center justify-between px-4 pb-4"
              style={{ paddingTop: insets.top || 24 }}
            >
              <BackButton />
              <Heading variant="card">Editar perfil</Heading>
              <Link href={"/user/settings"} asChild>
                <IconButton
                  shape="circle"
                  surface="transparent"
                  accessibilityLabel="Settings"
                >
                  <Ionicons
                    name="settings-outline"
                    size={24}
                    color={foreground}
                  />
                </IconButton>
              </Link>
            </View>

            {/* Avatar */}
            <Controller
              control={control}
              name="avatarUrl"
              render={({ field: { onChange, value } }) => {
                const avatarUri = getAvatarUri(value);
                const initials =
                  profile.displayName?.[0]?.toUpperCase() ||
                  profile.username[0]?.toUpperCase() ||
                  "?";

                return (
                  <AvatarPicker
                    avatarUri={avatarUri}
                    onChangeAvatar={onChange}
                    initials={initials}
                  />
                );
              }}
            />

            {/* Form Card */}
            <View className="mx-6 px-6 py-2">
              {/* Email (read-only) */}
              <View className="mb-4 w-full">
                <View className="bg-card-event border-b border-white px-4 pt-3 pb-2 opacity-60">
                  <Text className="text-muted-foreground mb-1 text-xs">
                    Email
                  </Text>
                  <View className="flex-row items-center justify-between">
                    <Text className="text-foreground text-base leading-tight">
                      {session?.user.email}
                    </Text>
                    <Ionicons
                      name="lock-closed-outline"
                      size={14}
                      color={mutedForeground}
                    />
                  </View>
                </View>
              </View>

              {/* Display Name */}
              <FormInput
                control={control}
                name="displayName"
                label="Nome"
                placeholder="Seu nome"
                autoCapitalize="words"
              />

              {/* Username */}
              <FormInput
                control={control}
                name="username"
                label="Username"
                placeholder="username"
                prefix="@"
                autoComplete="username"
              />

              <FormSelect
                control={control}
                name="state"
                label="Estado"
                placeholder="Selecione o estado"
                options={stateOptions}
                isLoading={isLoadingStates}
              />

              <FormSelect
                control={control}
                name="city"
                label="Cidade"
                placeholder="Selecione a cidade"
                options={cityOptions}
                isLoading={isLoadingCities}
                disabled={!selectedState}
                searchable
                searchPlaceholder="Buscar cidade..."
              />

              {/* Instagram */}
              <FormInput
                control={control}
                name="instagramUsername"
                label="Instagram"
                placeholder="seuinstagram"
                prefix="@"
              />

              {/* Show Instagram toggle */}
              <Controller
                control={control}
                name="shouldShowInstagram"
                render={({ field: { value, onChange } }) => (
                  <Toggle
                    value={value ?? false}
                    onValueChange={onChange}
                    label="Exibir Instagram no perfil"
                    description="Outras usuárias poderão ver seu @"
                  />
                )}
              />

              {errors.root && (
                <Text className="text-destructive mt-2 text-sm">
                  {errors.root.message}
                </Text>
              )}
            </View>

            {/* Save Button */}
            <View className="mt-auto px-6 pt-8">
              <Button
                onPress={handleSubmit(onSubmit)}
                loading={updateProfile.isPending}
                className="h-14 rounded-2xl"
                type="submit"
              >
                Salvar
              </Button>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </ScreenContainer>
    </>
  );
}
